import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import {
  getAuth,
  setAuth as persistAuth,
  removeAuth,
  isAuthRoutePath,
  isPublicEntryPath,
  shouldBlockWithSessionSplash,
} from './AuthHelpers'
import {getUserByToken} from './_requests'
import {
  AUTH_SESSION_EXPIRED_EVENT,
  clearProactiveRefresh,
  initAuthStorageSync,
  isAccessTokenExpired,
  requestTokenRefresh,
  scheduleProactiveRefresh,
} from './TokenRefresh'
import {WithChildren} from '../../../../_metronic/helpers'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()

  const saveAuth = (nextAuth: AuthModel | undefined) => {
    setAuth(nextAuth)
    if (nextAuth) {
      persistAuth(nextAuth)
      scheduleProactiveRefresh()
    } else {
      removeAuth()
      clearProactiveRefresh()
    }
  }

  const logout = () => {
    clearProactiveRefresh()
    saveAuth(undefined)
    setCurrentUser(undefined)
  }

  useEffect(() => {
    initAuthStorageSync()

    const syncAuthState = () => {
      setAuth(getAuth())
    }

    const handleSessionExpired = () => {
      logout()
    }

    window.addEventListener('auth-updated', syncAuthState)
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired)

    const storedAuth = getAuth()
    if (storedAuth?.token && !isPublicEntryPath()) {
      scheduleProactiveRefresh()
    }

    return () => {
      window.removeEventListener('auth-updated', syncAuthState)
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired)
      clearProactiveRefresh()
    }
    // eslint-disable-next-line
  }, [])

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {logout, setCurrentUser} = useAuth()
  const [showSplashScreen, setShowSplashScreen] = useState(() => shouldBlockWithSessionSplash())

  useEffect(() => {
    const pathname = window.location.pathname
    const storedAuth = getAuth()
    const hasStoredToken = Boolean(storedAuth?.token)

    const finishSplash = () => {
      setShowSplashScreen(false)
    }

    const restoreSession = async () => {
      try {
        const activeAuth = getAuth()
        if (!activeAuth?.token) {
          return
        }

        if (isAccessTokenExpired(activeAuth.token)) {
          await requestTokenRefresh()
        }

        if (!getAuth()?.token) {
          return
        }

        const {data} = await getUserByToken()
        if (data) {
          setCurrentUser(data)
        }
      } catch (error) {
        console.error(error)
        if (getAuth()) {
          logout()
        }
      } finally {
        finishSplash()
      }
    }

    if (isAuthRoutePath(pathname)) {
      if (hasStoredToken) {
        logout()
      }
      finishSplash()
      return
    }

    if (!hasStoredToken) {
      logout()
      finishSplash()
      return
    }

    restoreSession()
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
